import { addActor, after, between, budget, rand, spawn } from '../engine';
import { burst, ease, explode, glints, launch, light, rain, shock, wash, type Stage } from '../kit';
import { paper, stamp, twinkle, wedge } from '../shapes';
import { TAU, UP, type Preset } from './types';

/* ===================================== Liftoff: fireworks along the rocket's climb ===================================== */

const LIFT = ['#2EE6C9', '#FF8A3D', '#FFD166', '#5B7CFA', '#F07AA8'] as const;

/** The Liftoff curve: an exponential climb from the lower left to the upper right. */
function curve(u: number, W: number, H: number): { x: number; y: number } {
  const e = (Math.exp(3 * u) - 1) / (Math.exp(3) - 1);
  return { x: W * (0.1 + 0.8 * u), y: H * (0.86 - 0.66 * e) };
}

function climbTrail(s: Stage, dur: number): void {
  let acc = 0;
  addActor({
    dur,
    draw(g, k, f) {
      const q = ease.out(Math.min(1, k * 1.4));
      g.globalCompositeOperation = 'lighter';
      for (const [w, al] of [
        [12, 0.08],
        [3, 0.5],
      ] as const) {
        g.globalAlpha = al * ease.env(k, 0.05, 0.7);
        g.strokeStyle = '#2EE6C9';
        g.lineWidth = w;
        g.lineCap = 'round';
        g.beginPath();
        for (let i = 0; i <= 40; i++) {
          const p = curve((i / 40) * q, f.W, f.H);
          if (i === 0) g.moveTo(p.x, p.y);
          else g.lineTo(p.x, p.y);
        }
        g.stroke();
      }
      if (k < 0.7) {
        acc += f.dt * 50;
        while (acc >= 1) {
          acc -= 1;
          const p = curve(q * rand(), f.W, f.H);
          spawn({
            x: p.x,
            y: p.y,
            vx: between(-20, 20),
            vy: between(10, 50),
            draw: twinkle,
            col: rand() < 0.5 ? '#FFFFFF' : '#FFD166',
            size: between(2, 4),
            life: between(0.5, 0.9),
            add: true,
            vr: 2,
          });
        }
      }
    },
  });
}

export const crash: Preset = {
  pal: LIFT,
  light: '#FFFFFF',
  win(s) {
    for (let i = 0; i < 3; i++) {
      const col = LIFT[i % LIFT.length] ?? '#2EE6C9';
      launch(
        s,
        s.x,
        s.y,
        s.x + between(40, 140),
        s.y - between(120, 200),
        col,
        (x, y) => explode(s, x, y, { col, n: 22, speed: 170, sound: null, life: [0.35, 0.55] }),
        i * 90,
        0.3,
      );
    }
    glints(s, s.x, s.y, 6, 20);
  },
  big(s) {
    climbTrail(s, 2.2);
    for (let i = 0; i < 6; i++) {
      const p = curve((i + 1.4) / 7.4, s.W, s.H);
      const col = LIFT[i % LIFT.length] ?? '#2EE6C9';
      launch(
        s,
        p.x + between(-30, 30),
        s.H + 10,
        p.x,
        p.y - 40,
        col,
        (x, y) => explode(s, x, y, { col, n: 64, speed: 300 }),
        120 + i * 220,
      );
    }
  },
  mega(s) {
    crash.big(s);
    after(1500, () => {
      const p = curve(0.96, s.W, s.H);
      for (const [i, col] of ['#FFD166', '#2EE6C9', '#FF8A3D'].entries())
        after(i * 160, () =>
          explode(s, p.x - i * 60, p.y + 20 + i * 30, { col, n: 110, speed: 480, ring: true }),
        );
      wash(p.x, p.y, { col: '#FFD166', r: Math.max(s.W, s.H) * 0.45, dur: 1.8, alpha: 0.35 });
      // glitter falls after the finale
      after(500, () =>
        rain(s, {
          n: 120,
          over: 900,
          band: [0.45, 1],
          vy: [30, 90],
          draw: twinkle,
          cols: ['#FFFFFF', '#FFD166', '#FFE7A3'],
          size: [2, 4],
          life: [1.2, 1.8],
          gravity: 60,
          add: true,
          vr: [-4, 4],
        }),
      );
    });
  },
};

/* ===================================== Pot: the wheel flies apart in the winner's colour ===================================== */

function wedges(
  s: Stage,
  n: number,
  speed: readonly [number, number],
  size: readonly [number, number],
  life: readonly [number, number],
): void {
  burst(s, s.x, s.y, {
    n,
    speed,
    draw: wedge,
    col2: '#FFFFFF',
    size,
    life,
    gravity: 420,
    drag: 0.55,
    vr: [-6, 6],
    flip: [0, 3],
  });
}

function wheelBreak(s: Stage): void {
  const n = 24;
  addActor({
    dur: 0.55,
    front: true,
    draw(g, k, f) {
      const R = Math.min(f.W, f.H) * 0.16 * ease.back(Math.min(1, k * 2.2));
      const spin = f.t * 9;
      for (let i = 0; i < n; i++) {
        const an = (i / n) * TAU + spin;
        stamp(
          g,
          wedge,
          s.x + Math.cos(an) * R * 0.55,
          s.y + Math.sin(an) * R * 0.55,
          R * 0.45,
          an,
          s.pal[i % s.pal.length] ?? '#FFD166',
          '#FFFFFF',
          f.dpr,
        );
      }
    },
  });
  after(540, () => {
    for (let i = 0; i < budget(n); i++) {
      const an = (i / n) * TAU;
      const v = between(380, 640);
      spawn({
        x: s.x + Math.cos(an) * 30,
        y: s.y + Math.sin(an) * 30,
        vx: Math.cos(an) * v,
        vy: Math.sin(an) * v,
        draw: wedge,
        col: s.pal[i % s.pal.length] ?? '#FFD166',
        col2: '#FFFFFF',
        size: Math.min(s.W, s.H) * 0.07,
        life: 1.8,
        gravity: 380,
        drag: 0.6,
        rot: an,
        vr: between(-5, 5),
      });
    }
    shock(s.x, s.y, { col: s.pal[0] ?? '#FFD166', r: Math.max(s.W, s.H) * 0.45, dur: 1.1, width: 6 });
    light(s, s.x, s.y, 200, s.pal[0] ?? '#FFD166', 0.8, 0.6);
  });
}

export const pot: Preset = {
  pal: ['#FFD166', '#F2B33D', '#FFE7A3', '#FF8A3D'],
  light: '#FFFFFF',
  win(s) {
    wedges(s, 10, [160, 320], [10, 14], [0.7, 0.95]);
    glints(s, s.x, s.y, 8, 30);
  },
  big(s) {
    wedges(s, 36, [300, 720], [16, 26], [1.6, 2]);
    shock(s.x, s.y, { col: s.pal[0] ?? '#FFD166', r: Math.min(s.W, s.H) * 0.4, dur: 0.9, width: 5 });
    rain(s, {
      n: 120,
      over: 1200,
      vy: [140, 240],
      draw: paper,
      size: [5, 8],
      life: [2, 2.4],
      sway: [15, 30],
      flip: [5, 10],
      vr: [-3, 3],
      gravity: 80,
    });
    light(s, s.x, s.y, 140, s.pal[0] ?? '#FFD166', 0.7, 0.5);
  },
  mega(s) {
    wheelBreak(s);
    after(500, () => {
      wedges(s, 30, [300, 760], [16, 26], [1.8, 2.2]);
      rain(s, {
        n: 160,
        over: 1800,
        vy: [140, 240],
        draw: paper,
        size: [5, 8],
        life: [2, 2.4],
        sway: [15, 30],
        flip: [5, 10],
        vr: [-3, 3],
        gravity: 80,
      });
      wash(s.x, s.y, { col: s.pal[0] ?? '#FFD166', r: Math.max(s.W, s.H) * 0.5, dur: 2.4, alpha: 0.3 });
    });
  },
};

/* ===================================== Roulette: confetti of the winning sector's colour ===================================== */

/** Sector palettes; the caller passes one of them (or its own) as `palette`. */
export const ROULETTE_COLORS = {
  red: ['#E5484D', '#FF7A7E', '#B3262C', '#FFD6D7'],
  black: ['#2A3142', '#4A5873', '#AAB6CC', '#E4EAF4'],
  green: ['#2EE6C9', '#7BE07B', '#1F9E6E', '#FFD166'],
} as const;

function cannon(s: Stage, left: boolean, n: number, over: number): void {
  burst(s, left ? 0 : s.W, s.H + 10, {
    n,
    over,
    speed: [720, 1100],
    angle: UP + (left ? 0.5 : -0.5),
    spread: 0.55,
    draw: paper,
    size: [5, 9],
    life: [2, 2.6],
    gravity: 620,
    drag: 0.55,
    flip: [5, 11],
    vr: [-4, 4],
    sway: [10, 30],
  });
}

export const roulette: Preset = {
  pal: ROULETTE_COLORS.green,
  light: '#FFFFFF',
  win(s) {
    burst(s, s.x, s.y, {
      n: 40,
      speed: [200, 420],
      angle: UP,
      spread: 1.6,
      draw: paper,
      size: [4.5, 7],
      life: [0.75, 0.95],
      gravity: 640,
      drag: 0.5,
      flip: [6, 11],
      vr: [-5, 5],
    });
    glints(s, s.x, s.y, 6, 24);
  },
  big(s) {
    cannon(s, true, 130, 500);
    cannon(s, false, 130, 500);
    rain(s, {
      n: 80,
      over: 1200,
      vy: [120, 220],
      draw: paper,
      size: [5, 8],
      life: [2, 2.4],
      sway: [15, 30],
      flip: [5, 10],
      vr: [-3, 3],
      gravity: 80,
    });
    glints(s, s.x, s.y, 16, 40);
    light(s, s.x, s.y, 120, s.pal[0] ?? '#2EE6C9', 0.7, 0.5);
  },
  mega(s) {
    roulette.big(s);
    for (let i = 0; i < 4; i++) {
      const col = s.pal[i % s.pal.length] ?? '#2EE6C9';
      const x = s.W * (0.2 + 0.2 * i);
      launch(
        s,
        x,
        s.H + 10,
        x + between(-40, 40),
        s.H * between(0.2, 0.35),
        col,
        (ex, ey) => explode(s, ex, ey, { col, n: 80, speed: 380, ring: i % 2 === 0 }),
        600 + i * 240,
      );
    }
  },
};

/* ===================================== the shared default (unknown games, tourney prizes) ===================================== */

export const fallback: Preset = {
  pal: ['#2EE6C9', '#FF8A3D', '#FFD166', '#5B7CFA', '#F07AA8', '#7BE07B'],
  light: '#FFFFFF',
  win(s) {
    burst(s, s.x, s.y, {
      n: 36,
      speed: [180, 400],
      angle: UP,
      spread: 1.7,
      draw: paper,
      size: [4.5, 7],
      life: [0.75, 0.95],
      gravity: 640,
      drag: 0.5,
      flip: [6, 11],
      vr: [-5, 5],
    });
    glints(s, s.x, s.y, 6, 24);
  },
  big(s) {
    rain(s, {
      n: 200,
      over: 1300,
      vy: [120, 240],
      draw: paper,
      size: [5, 8],
      life: [2, 2.4],
      sway: [15, 30],
      flip: [5, 10],
      vr: [-3, 3],
      gravity: 80,
    });
    for (let i = 0; i < 4; i++) {
      const col = fallback.pal[i] ?? '#2EE6C9';
      const x = s.W * (0.2 + 0.2 * i);
      launch(
        s,
        x,
        s.H + 10,
        x,
        s.H * between(0.2, 0.4),
        col,
        (ex, ey) => explode(s, ex, ey, { col, n: 64, speed: 320 }),
        i * 240,
      );
    }
  },
  mega(s) {
    fallback.big(s);
    after(1100, () => {
      for (let i = 0; i < 3; i++) {
        const col = fallback.pal[(i + 2) % 6] ?? '#FFD166';
        explode(s, s.W * (0.3 + 0.2 * i), s.H * 0.3, { col, n: 100, speed: 440, ring: true });
      }
    });
  },
};
